import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx } from "../schema"
import type {
	HelpCenterGroupTabMove,
	HelpCenterNavStyleUpdate,
	HelpCenterTabCreate,
	HelpCenterTabReorder,
	HelpCenterTabUpdate,
} from "../types"

// API reference tabs are created and deleted over tRPC, which also fetches the spec,
// stores it, and clears its search entries. These mutators only handle docs tabs.
export const helpCenterTabMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HelpCenterTabCreate
			ctx: ZeroJwtPayload
		}) => {
			const now = Date.now()
			await tx.mutate.helpCenterTabs.insert({
				id: args.id,
				name: args.name,
				icon: args.icon,
				order: args.order,
				kind: "DOCS",
				translations: {},
				// Readers see a tab only once someone publishes it.
				isPublished: false,
				workspaceId: authData.workspaceId,
				portalInstanceId: args.portalInstanceId ?? null,
				createdAt: now,
				updatedAt: now,
			})
		},
	),
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: HelpCenterTabUpdate }) => {
			await tx.mutate.helpCenterTabs.update({
				id: args.id,
				...(args.name !== undefined ? { name: args.name } : {}),
				...(args.icon !== undefined ? { icon: args.icon } : {}),
				...(args.translations !== undefined
					? { translations: args.translations }
					: {}),
				...(args.isPublished !== undefined
					? { isPublished: args.isPublished }
					: {}),
				updatedAt: Date.now(),
			})
		},
	),
	// Groups fall back to the default tab, so nothing disappears from readers.
	delete: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: { id: string } }) => {
			const groups = await tx.query.helpCenterGroups
				.where("tabId", args.id)
				.run()
			for (const group of groups) {
				await tx.mutate.helpCenterGroups.update({ id: group.id, tabId: null })
			}
			await tx.mutate.helpCenterTabs.delete({ id: args.id })
		},
	),
	reorder: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: HelpCenterTabReorder }) => {
			for (const item of args.items) {
				await tx.mutate.helpCenterTabs.update({
					id: item.id,
					order: item.order,
				})
			}
		},
	),
	moveGroup: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: HelpCenterGroupTabMove }) => {
			await tx.mutate.helpCenterGroups.update({
				id: args.groupId,
				tabId: args.tabId,
			})
		},
	),
	setNavStyle: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HelpCenterNavStyleUpdate
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.workspaces.update({
				id: authData.workspaceId,
				helpCenterNavStyle: args.style,
			})
		},
	),
}
